import { createSelector } from 'reselect';
import { List, Map } from 'immutable';

import { nullIfFirstArgNegative, nullIfArgByIndexesNegative, fromOwnProp } from '@Utils';

import { databases } from '@Services/UserDatabases/UserDatabasesSelectors';

import { alertsReducerKey } from './AlertsConstants';

export function alerts(state) {
  return state?.get(alertsReducerKey);
}

export function alertsIds(state) {
  const alertsState = alerts(state);

  return alertsState?.get('ids');
}

export function alertsItems(state) {
  const alertsState = alerts(state);

  return alertsState?.get('items');
}

export const alertById = createSelector(
  [alertsItems, fromOwnProp('id')],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItems, id) => alertsItems.get(id))
);

export const alertDatabaseId = createSelector(
  [alertById],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertById) => alertById.get('databaseId'))
);

export const alertDbName = createSelector(
  [alertDatabaseId, databases],
  nullIfArgByIndexesNegative(
    // eslint-disable-next-line no-shadow
    (alertDatabaseId, databases) => databases.getIn([alertDatabaseId, 'name']),
    [0, 1]
  )
);

export const alertsItem = createSelector(
  [alertById],
  nullIfFirstArgNegative((alert) => alert.get('alertsItem'))
);

export const alertContext = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => alertsItem.get('context'))
);

export const alertLastSaved = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => alertsItem.get('lastSaved'))
);

export const alertUuid = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => alertsItem.get('alertUuid'))
);

export const alertName = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => alertsItem.get('alertName'))
);

export const alertDescription = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => alertsItem.get('description'))
);

export const alertStructures = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => {
    const facts = alertsItem.getIn(['queryFactItem', 'facts'], List());

    return facts.reduce((res, fact) => {
      const structures = fact.get('structures', List());

      return res.concat(structures);
    }, List());
  })
);

export const alertResults = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => alertsItem.get('results'))
);

// returns Map
export const alertsGroupsMap = createSelector(
  [alertsIds, alertsItems],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsIds, alertsItems) =>
    alertsIds.reduce((GroupsMap, id) => {
      const groupId = alertsItems.getIn([id, 'alertsItem', 'groupId']);
      const GroupList = GroupsMap.get(groupId, List());

      return GroupsMap.set(groupId, GroupList.push(id));
    }, Map())
  )
);

// returns List
export const groupsIds = createSelector(
  [alertsGroupsMap],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsGroupsMap) => {
    const groupKeys = alertsGroupsMap.keySeq();

    return groupKeys?.toList();
  })
);

// returns List
export const alertsIdsByGroupId = createSelector(
  [alertsGroupsMap, fromOwnProp('groupId')],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsGroupsMap, groupId) => alertsGroupsMap.get(groupId))
);

// returns List
export const allDatabasesByGroupId = createSelector(
  [alertsItems, alertsGroupsMap, fromOwnProp('groupId')],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItems, alertsGroupsMap, groupId) => {
    const GroupList = alertsGroupsMap.get(groupId);

    return GroupList && GroupList.map((id) => alertsItems.getIn([id, 'databaseId']));
  })
);

export function isMounted(state) {
  const alertsState = alerts(state);

  return alertsState?.get('mounted');
}

export function refetch(state) {
  const alertsState = alerts(state);

  return alertsState?.get('refetch');
}

export function showDeleteModal(state) {
  const alertsState = alerts(state);

  return alertsState?.get('showDeleteModal');
}

export function alertIdToDelete(state) {
  const alertsState = alerts(state);

  return alertsState?.get('alertIdToDelete');
}

export function isDeleteInProgress(state) {
  const alertsState = alerts(state);

  return alertsState?.get('isDeleteInProgress');
}

export function deletingAlertId(state) {
  const alertsState = alerts(state);

  return alertsState?.get('deletingAlertId');
}
